import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/authService';

const AuthContext = createContext();

const AUTH_STORAGE_KEYS = ['token', 'role', 'id', 'name', 'password_changed', 'markaz_id', 'markaz_name', 'financials_enabled'];

// Keep all login-related localStorage keys in one list so logout and expired
// sessions fully clear stale auth state.
const clearStoredAuth = () => {
    AUTH_STORAGE_KEYS.forEach(key => localStorage.removeItem(key));
};

// Basic JWT expiration check used before trusting localStorage on page reload.
// The backend still performs the real authorization check on every API call.
const isTokenUsable = (token) => {
    if (!token) return false;

    try {
        const [, payload] = token.split('.');
        if (!payload) return false;

        const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
        if (!decoded.exp) return true;

        return decoded.exp * 1000 > Date.now();
    } catch (err) {
        return false;
    }
};

const getStoredUser = () => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    const id = localStorage.getItem('id');
    const name = localStorage.getItem('name');
    const password_changed = localStorage.getItem('password_changed');
    const markaz_id = localStorage.getItem('markaz_id');
    const markaz_name = localStorage.getItem('markaz_name');
    const financials_enabled = localStorage.getItem('financials_enabled');

    if (!token || !role || !id || !isTokenUsable(token)) {
        clearStoredAuth();
        return null;
    }

    // currentUser is the frontend's session snapshot. Backend routes should
    // still use the JWT, not these localStorage values, for security decisions.
    return { token, role, id, name, password_changed, markaz_id, markaz_name, financials_enabled: financials_enabled === 'true' };
};

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(() => {
        return getStoredUser();
    });

    useEffect(() => {
        setCurrentUser(getStoredUser());
    }, []);
    

    const login = async (email, password) => {
        try {
            const response = await authService.login(email, password);
            const { token, role, id, name, password_changed, markaz_id, markaz_name, financials_enabled } = response;

            if (token && role && id) {
                localStorage.setItem('token', token);
                localStorage.setItem('role', role);
                localStorage.setItem('id', id);
                localStorage.setItem('name', name);
                localStorage.setItem('password_changed', password_changed);
                localStorage.setItem('markaz_id', markaz_id);
                localStorage.setItem('markaz_name', markaz_name || '');
                localStorage.setItem('financials_enabled', financials_enabled ? 'true' : 'false');

                setCurrentUser({ token, role, id, name, password_changed, markaz_id, markaz_name, financials_enabled });
            }

            return response;
        } catch (error) {
            console.error('Login failed:', error);
            throw error; // ✅ Let LandingPage handle it
          }
    };

    const logout = () => {
        clearStoredAuth();
        setCurrentUser(null);
    };

    return (
        <AuthContext.Provider value={{ currentUser, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
